const API_BASE_URL = '/api';
const LOCAL_CONFIG_KEY = 'diekleinedekoecke_site_config';

function getApiHeaders(extraHeaders = {}) {
  return {
    'Content-Type': 'application/json',
    ...extraHeaders
  };
}

async function parseErrorMessage(response) {
  try {
    const data = await response.json();
    return data?.message || data?.error || response.statusText;
  } catch (_) {
    return response.statusText;
  }
}

async function apiRequest(path, { method = 'GET', body, headers = {} } = {}) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: getApiHeaders(headers),
    body: body === undefined ? undefined : JSON.stringify(body)
  });

  if (!response.ok) {
    const message = await parseErrorMessage(response);
    throw new Error(message || `HTTP ${response.status}`);
  }

  if (response.status === 204) {
    return null;
  }

  const text = await response.text();
  return text ? JSON.parse(text) : null;
}

function normalizeProductImages(product) {
  const normalized = { ...product };
  if (Array.isArray(normalized.images)) return normalized;
  if (typeof normalized.images === 'string' && normalized.images.trim()) {
    try {
      const parsed = JSON.parse(normalized.images);
      normalized.images = Array.isArray(parsed) ? parsed : [];
      return normalized;
    } catch (_) {
      normalized.images = [];
      return normalized;
    }
  }
  normalized.images = normalized.image ? [normalized.image] : [];
  normalized.tags = Array.isArray(normalized.tags) ? normalized.tags : [];
  return normalized;
}

export async function getProducts() {
  const rows = await apiRequest('/products');
  return Array.isArray(rows) ? rows.map(normalizeProductImages) : [];
}

export async function getProductById(id) {
  const product = await apiRequest(`/products/${encodeURIComponent(id)}`);
  if (!product) {
    throw new Error('Produkt nicht gefunden');
  }
  return normalizeProductImages(product);
}

export async function createProduct(productData) {
  const data = await apiRequest('/products', {
    method: 'POST',
    body: productData
  });
  if (!data?.product) throw new Error('Produkt konnte nicht erstellt werden');
  return normalizeProductImages(data.product);
}

export async function updateProductById(id, productData) {
  const data = await apiRequest(`/products/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: productData
  });
  if (!data?.product) throw new Error('Produkt konnte nicht aktualisiert werden');
  return normalizeProductImages(data.product);
}

export async function deleteProductById(id) {
  await apiRequest(`/products/${encodeURIComponent(id)}`, {
    method: 'DELETE'
  });
}

export async function getTags() {
  const rows = await apiRequest('/tags');
  return Array.isArray(rows) ? rows : [];
}

export async function createTag(tagData) {
  const data = await apiRequest('/tags', {
    method: 'POST',
    body: tagData
  });
  if (!data?.tag) throw new Error('Tag konnte nicht erstellt werden');
  return data.tag;
}

export async function updateTagById(id, tagData) {
  const data = await apiRequest(`/tags/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: tagData
  });
  if (!data?.tag) throw new Error('Tag konnte nicht aktualisiert werden');
  return data.tag;
}

export async function deleteTagById(id) {
  await apiRequest(`/tags/${encodeURIComponent(id)}`, {
    method: 'DELETE'
  });
}

export async function getUserByUsername(username) {
  return await apiRequest(`/users/${encodeURIComponent(username)}`);
}

export async function verifyAdminByUsername(username) {
  const data = await apiRequest('/users/verify-admin', {
    method: 'POST',
    body: { username }
  });
  return Boolean(data?.isAdmin);
}

export async function loginAdmin(username, passwordHash) {
  return await apiRequest('/users/login', {
    method: 'POST',
    body: { username, password: passwordHash }
  });
}

export async function updateUserByUsername(username, updatedData) {
  const data = await apiRequest(`/users/${encodeURIComponent(username)}`, {
    method: 'PUT',
    body: updatedData
  });
  if (!data?.user) throw new Error('Benutzer nicht gefunden.');
  return data.user;
}

export async function updateUserPassword(username, currentPasswordHash, newPasswordHash) {
  const data = await apiRequest(`/users/${encodeURIComponent(username)}`, {
    method: 'PUT',
    body: {
      currentPassword: currentPasswordHash,
      password: newPasswordHash
    }
  });
  if (!data?.ok) throw new Error('Passwort konnte nicht aktualisiert werden.');
  return data.user || null;
}

export async function deleteUserByUsername(username) {
  await apiRequest(`/users/${encodeURIComponent(username)}`, {
    method: 'DELETE'
  });
}

export async function loadSiteConfig() {
  try {
    const config = await apiRequest('/config');
    if (config) {
      localStorage.setItem(LOCAL_CONFIG_KEY, JSON.stringify(config));
      return config;
    }
  } catch (err) {
    console.warn('Config konnte nicht vom Server geladen werden, Fallback aktiv:', err?.message || err);
  }

  const local = localStorage.getItem(LOCAL_CONFIG_KEY);
  if (local) {
    try {
      return JSON.parse(local);
    } catch (_) {
      localStorage.removeItem(LOCAL_CONFIG_KEY);
    }
  }

  const fallbackRes = await fetch('/config.json');
  if (!fallbackRes.ok) throw new Error('Konfiguration konnte nicht geladen werden.');
  return await fallbackRes.json();
}

export async function saveSiteConfig(config) {
  await apiRequest('/config', {
    method: 'POST',
    body: config
  });
  localStorage.setItem(LOCAL_CONFIG_KEY, JSON.stringify(config));
  return config;
}
